import { useEffect, useState, type CSSProperties } from 'react';
import {
  taxReportJ05, type TaxFormView, type TaxFormScalar, type TaxFormTable,
  type TaxFormColumn,
} from '@/api/taxReportJ05';

/**
 * Форма-бланк Податкового розрахунку — візуально наближена до ОФІЦІЙНОЇ
 * друкованої форми (наказ Мінфіну №4 у ред. №39): чорні рамки, шрифт з
 * зарубками, ліворуч «код рядка», далі назва показника, праворуч поле
 * значення. Значення автозаповнені (той самий код, що генерує XML) і
 * редаговані прямо у бланку; жовте поле — виправлено вручну, ↺ повертає авто.
 */

/** Короткі назви вкладок форм пакета. */
const TAB_NAME: Record<string, string> = {
  MAIN: 'Основний', D1: 'Д1', D2: 'Д2', D3: 'Д3',
  '4DF': '4ДФ', D5: 'Д5', D6: 'Д6',
};

// --- Стилі бланка (чорні рамки, зарубковий шрифт як у офіційній формі) ---
const SERIF = "'Times New Roman', 'PT Serif', Georgia, serif";
const B = '1px solid #333';
const blankBox: CSSProperties = { fontFamily: SERIF, color: '#111',
  border: B, background: '#fff' };
const numCell: CSSProperties = { border: B, padding: '2px 5px',
  textAlign: 'center', verticalAlign: 'middle', whiteSpace: 'nowrap',
  fontWeight: 600, width: 46, fontSize: '0.78rem' };
const nameCell: CSSProperties = { border: B, padding: '2px 6px',
  verticalAlign: 'middle', fontSize: '0.8rem', lineHeight: 1.3 };
const valCell: CSSProperties = { border: B, padding: '1px 3px',
  verticalAlign: 'middle', width: 190 };
/** Стиль поля з даними, які НЕ редагуються руками (реквізити) — сірий фон. */
const roStyle: CSSProperties = { display: 'block', padding: '2px 4px',
  fontWeight: 600, background: '#eef1f4', color: '#334155' };
/** Шапка таблиці бланка: глобальний `table th` у styles.css робить ВЕЛИКІ
 * літери + letter-spacing + сірий колір — для бланка ДПС це неправильно
 * (назви граф — звичайним регістром, як у формі). Скидаємо явно. */
const thBlank: CSSProperties = { textTransform: 'none', letterSpacing: 'normal',
  fontWeight: 400, fontSize: '0.6rem', color: '#111' };

/** Коди-відмітки: у бланку проставляється «X», у XML — значення 1
 * (тип 011/012/013, наявність додатків 06x, ознака платника 09x). */
const X_MARK_CODES = new Set([
  'HZ', 'HZN', 'HZU',
  'R061G3', 'R062G3', 'R063G3', 'R064G3', 'R065G3', 'R066G3',
  'R091G3', 'R092G3', 'R093G3', 'R094G3', 'R095G3',
  // v11 (J0500111): ознаки платника — блок 08 (у v11 R09x — чисельність,
  // але read-only вони не бувають, тож перетину з X-логікою немає).
  'R081G3', 'R082G3', 'R083G3', 'R084G3', 'R085G3',
]);

/** Відображення read-only значення (X-відмітки, дати). */
const roDisplay = (code: string, s: TaxFormScalar): string =>
  X_MARK_CODES.has(code)
    ? (s.value ? 'X' : '')
    : (s.kind === 'date' ? (dateToHuman(s.value) || '') : (s.value ?? ''));

/** Дата у XML — 8 цифр ДДММРРРР (тип DGDate); людині — ДД.ММ.РРРР. */
const dateToHuman = (v: string | null): string =>
  v && /^\d{8}$/.test(v) ? `${v.slice(0, 2)}.${v.slice(2, 4)}.${v.slice(4)}` : (v ?? '');
const dateToWire = (v: string): string => {
  const m = v.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  return m ? `${m[1]}${m[2]}${m[3]}` : v.trim();
};

/** Поле вводу, вбудоване у клітинку бланка (без власної рамки — рамка клітинки). */
function BlankInput({
  value, auto, isManual, kind = 'text', onSave, onReset, align = 'left', error,
}: {
  value: string | null;
  auto: string | null;
  isManual: boolean;
  kind?: 'text' | 'date';
  onSave: (v: string) => Promise<void>;
  onReset: () => Promise<void>;
  align?: 'left' | 'right' | 'center';
  error?: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const display = kind === 'date' ? dateToHuman(value) : (value ?? '');
  const autoDisplay = kind === 'date' ? dateToHuman(auto) : auto;
  const shown = draft ?? display;
  const dirty = draft !== null && draft !== display;

  async function save() {
    if (!dirty) { setDraft(null); return; }
    setBusy(true);
    try { await onSave(kind === 'date' ? dateToWire(draft!) : draft!); setDraft(null); }
    finally { setBusy(false); }
  }

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
      <input
        value={shown}
        disabled={busy}
        placeholder={kind === 'date' ? 'ДД.ММ.РРРР' : undefined}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        style={{
          width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
          fontFamily: SERIF, fontSize: '0.82rem', textAlign: align,
          padding: '2px 4px',
          // редаговане поле — легкий жовтий; ручна правка — насичений жовтий
          background: isManual ? '#fde68a' : '#fefce8',
          fontWeight: isManual ? 700 : 400, color: '#111',
          // поле з помилкою перевірки — червона рамка (фон зберігається)
          boxShadow: error ? 'inset 0 0 0 2px #dc2626' : undefined,
        }}
        title={isManual ? `Виправлено вручну (авто: ${autoDisplay ?? '—'})` : undefined}
      />
      {isManual && (
        <button
          disabled={busy}
          onClick={async () => { setBusy(true); try { await onReset(); } finally { setBusy(false); } }}
          title={`Повернути авто-значення (${autoDisplay ?? '—'})`}
          style={{ border: 'none', background: 'none', cursor: 'pointer',
            padding: '0 2px', fontSize: '0.72rem', color: '#b45309' }}
        >↺</button>
      )}
    </span>
  );
}

const isNum = (s: string | null): boolean =>
  !!s && /^-?\d[\d\s.,]*$/.test(s.trim());

/** Рядок бланка «код | назва | значення» (для шапки та розділів). */
function BlankRow({
  s, onSave, onReset, error,
}: {
  s: TaxFormScalar;
  onSave: (code: string, v: string) => Promise<void>;
  onReset: (code: string) => Promise<void>;
  error?: boolean;
}) {
  return (
    <tr>
      <td style={numCell} title={s.code}>{s.num ?? ''}</td>
      <td style={nameCell}>{s.label}</td>
      <td style={valCell}>
        {s.editable ? (
          <BlankInput
            value={s.value} auto={s.auto} isManual={s.is_manual} kind={s.kind}
            align={s.kind === 'date' ? 'center' : (isNum(s.value) ? 'right' : 'left')}
            onSave={(v) => onSave(s.code, v)}
            onReset={() => onReset(s.code)}
            error={error}
          />
        ) : (
          <span style={{ ...roStyle, textAlign: isNum(s.value) ? 'right' : 'left' }}
            title={s.value ?? ''}>
            {s.kind === 'date' ? (dateToHuman(s.value) || '—') : (s.value ?? '—')}
          </span>
        )}
      </td>
    </tr>
  );
}

/** Секційна шапка ОСНОВНОЇ форми J0500110 — розкладка блоків 01–10
 * точно за офіційним бланком (номер | назва | значення), з особливими
 * блоками: 02 період (3 поля в ряд), 04 адреса+контакти, 06 додатки
 * (наявність|аркушів), 09 ознака платника. */
function MainHeader({
  byCode, save, reset, isErr,
}: {
  byCode: Map<string, TaxFormScalar>;
  save: (code: string, v: string) => Promise<void>;
  reset: (code: string) => Promise<void>;
  isErr?: (field: string, row?: number) => boolean;
}) {
  const inp = (code: string, align: 'left' | 'right' | 'center' = 'left') => {
    const s = byCode.get(code);
    if (!s) return null;
    if (!s.editable) {
      return (
        <span style={{ ...roStyle, textAlign: align }} title={s.value ?? ''}>
          {roDisplay(code, s)}
        </span>
      );
    }
    return (
      <BlankInput value={s.value} auto={s.auto} isManual={s.is_manual}
        kind={s.kind} align={align} error={isErr?.(code)}
        onSave={(v) => save(code, v)} onReset={() => reset(code)} />
    );
  };
  const lbl = (code: string) => byCode.get(code)?.label ?? '';
  // рядок «код | назва | значення»
  const row = (code: string, opts: { align?: 'left' | 'right' | 'center' } = {}) => {
    const s = byCode.get(code);
    if (!s) return null;
    return (
      <tr>
        <td style={numCell} title={code}>{s.num ?? ''}</td>
        <td style={nameCell}>{s.label}</td>
        <td style={valCell}>{inp(code, opts.align ?? 'left')}</td>
      </tr>
    );
  };
  const sub: CSSProperties = { fontSize: '0.62rem', color: '#555',
    textAlign: 'center' };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, border: B }}>
      <colgroup>
        <col style={{ width: 46 }} /><col /><col style={{ width: 260 }} />
      </colgroup>
      <tbody>
        {/* 01 — назва форми + вибір типу 011/012/013 */}
        <tr>
          <td style={numCell}>01</td>
          <td style={nameCell}>
            Податковий розрахунок сум доходу, нарахованого (сплаченого) на
            користь платників податків — фізичних осіб, і сум утриманого з них
            податку, а також сум нарахованого єдиного внеску
          </td>
          <td style={{ ...valCell, padding: 0 }}>
            {(['HZ', 'HZN', 'HZU'] as const).map((c) => (
              <div key={c} style={{ display: 'flex', borderBottom: c !== 'HZU' ? B : 'none' }}>
                <div style={{ ...numCell, width: 34, border: 'none', borderRight: B }}>
                  {byCode.get(c)?.num}
                </div>
                <div style={{ flex: 1, padding: '2px 4px', fontSize: '0.74rem' }}>
                  {lbl(c).replace(/^0\d\d\s*/, '')}
                </div>
                <div style={{ width: 60, borderLeft: B }}>{inp(c, 'center')}</div>
              </div>
            ))}
          </td>
        </tr>
        {/* 02 — період: рік / місяць / номер */}
        <tr>
          <td style={numCell}>02</td>
          <td style={nameCell}>Звітний (податковий) період</td>
          <td style={{ ...valCell, padding: 0 }}>
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1, borderRight: B }}>{inp('HZY', 'center')}<div style={sub}>(рік)</div></div>
              <div style={{ flex: 1, borderRight: B }}>{inp('HZM', 'center')}<div style={sub}>(місяць)</div></div>
              <div style={{ flex: 1 }}>{inp('HNUM', 'center')}<div style={sub}>(номер)</div></div>
            </div>
          </td>
        </tr>
        {/* 03 — платник: назва + договір */}
        <tr>
          <td style={numCell}>03</td>
          <td style={nameCell} colSpan={2}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ whiteSpace: 'nowrap' }}>Платник</span>
              <span style={{ flex: 1 }}>{inp('HNAME')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
              <span style={{ fontSize: '0.72rem' }}>від</span>
              <span style={{ width: 110 }}>{inp('HDDGV', 'center')}</span>
              <span style={{ fontSize: '0.72rem' }}>№</span>
              <span style={{ width: 130 }}>{inp('HNDGV')}</span>
              <span style={{ fontSize: '0.6rem', color: '#666' }}>
                (найменування платника, дата та номер договору)
              </span>
            </div>
          </td>
        </tr>
        {row('HTIN')}
        {row('HKATOTTG')}
        {row('HNAME1')}
        {row('HTIN1')}
        {row('HFIL')}
        {/* 04 — адреса + контакти */}
        <tr>
          <td style={numCell}>04</td>
          <td style={{ ...nameCell, padding: 0 }} colSpan={2}>
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1, borderRight: B, padding: '2px 6px' }}>
                <div style={{ fontSize: '0.72rem', color: '#555' }}>Податкова адреса</div>
                {inp('HLOC')}
              </div>
              <div style={{ width: 320 }}>
                {(['HZIP', 'HTEL', 'HFAX', 'HEMAIL'] as const).map((c, i) => (
                  <div key={c} style={{ display: 'flex', borderBottom: i < 3 ? B : 'none' }}>
                    <div style={{ flex: 1, padding: '2px 6px', fontSize: '0.72rem' }}>{lbl(c)}</div>
                    <div style={{ width: 150, borderLeft: B }}>{inp(c)}</div>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
        {row('HSTI')}
        {/* 06 — додатки: наявність | аркушів */}
        <tr>
          <td style={numCell}>06</td>
          <td style={nameCell}>Інформація про додатки, що додаються до цього Розрахунку</td>
          <td style={{ ...valCell, padding: 0 }}>
            <div style={{ display: 'flex', ...sub, fontWeight: 600 }}>
              <div style={{ flex: 1, borderRight: B, padding: '2px' }}>наявність</div>
              <div style={{ flex: 1, padding: '2px' }}>аркушів</div>
            </div>
          </td>
        </tr>
        {(['061', '062', '063', '064', '065', '066'] as const).map((n) => {
          const g3 = `R0${n.slice(1)}G3`;
          const g4 = `R0${n.slice(1)}G4`;
          const s = byCode.get(g3);
          return (
            <tr key={n}>
              <td style={numCell}>{n}</td>
              <td style={nameCell}>{(s?.label ?? '').replace(/^\d+\s*/, '')}</td>
              <td style={{ ...valCell, padding: 0 }}>
                <div style={{ display: 'flex' }}>
                  <div style={{ flex: 1, borderRight: B }}>{inp(g3, 'center')}</div>
                  <div style={{ flex: 1 }}>{inp(g4, 'center')}</div>
                </div>
              </td>
            </tr>
          );
        })}
        {row('HKVED')}
        {byCode.has('R081G3') ? (
          /* v11 (J0500111, наказ №243): 08 — ознака платника (xs:choice),
             09 — чисельність, 10/11 — інші застраховані / нові робочі місця.
             Класу профризику (v10 R08G3) у v11 немає. */
          <>
            <tr>
              <td style={numCell}>08</td>
              <td style={nameCell}>Ознака платника єдиного внеску</td>
              <td style={{ ...valCell, ...sub }}>Відмітка "Х" про відповідну ознаку</td>
            </tr>
            {row('R081G3', { align: 'center' })}
            {row('R082G3', { align: 'center' })}
            {row('R083G3', { align: 'center' })}
            {row('R084G3', { align: 'center' })}
            {row('R085G3', { align: 'center' })}
            <tr>
              <td style={numCell}>09</td>
              <td style={nameCell}>Чисельність працівників</td>
              <td style={{ ...valCell, ...sub }}>Показники (в цілих одиницях)</td>
            </tr>
            {['R091G3', 'R0911G3', 'R0912G3', 'R092G3']
              .map((c) => row(c, { align: 'right' }))}
            {row('R101G3', { align: 'right' })}
            {row('R111G3', { align: 'right' })}
          </>
        ) : (
          <>
            {row('R08G3', { align: 'center' })}
            {/* 09 — ознака платника ЄСВ */}
            <tr>
              <td style={numCell}>09</td>
              <td style={nameCell}>Ознака платника єдиного внеску</td>
              <td style={{ ...valCell, ...sub }}>Відмітка "Х" про відповідну ознаку</td>
            </tr>
            {row('R091G3', { align: 'center' })}
            {row('R092G3', { align: 'center' })}
            {row('R093G3', { align: 'center' })}
            {row('R094G3', { align: 'center' })}
            {row('R095G3', { align: 'center' })}
            {/* 10 — чисельність */}
            <tr>
              <td style={numCell}>10</td>
              <td style={nameCell}>Чисельність працівників</td>
              <td style={{ ...valCell, ...sub }}>Показники (в цілих одиницях)</td>
            </tr>
            {['R101G3', 'R1011G3', 'R1012G3', 'R102G3', 'R103G3', 'R104G3',
              'R105G3', 'R106G3', 'R107G3', 'R108G3'].map((c) => row(c, { align: 'right' }))}
          </>
        )}
      </tbody>
    </table>
  );
}

/** Шапка ДОДАТКА (Д1/4ДФ/Д5/Д2/Д3/Д6) за бланком: тип 011/012/013, період,
 * платник, податковий номер (+ для 4ДФ — № порції, КАТОТТГ, лічильники). */
function AppendixHeader({
  form, byCode, save, reset, isErr,
}: {
  form: TaxFormView;
  byCode: Map<string, TaxFormScalar>;
  save: (code: string, v: string) => Promise<void>;
  reset: (code: string) => Promise<void>;
  isErr?: (field: string, row?: number) => boolean;
}) {
  const inp = (code: string, align: 'left' | 'right' | 'center' = 'left') => {
    const s = byCode.get(code);
    if (!s) return null;
    if (!s.editable) {
      return <span style={{ ...roStyle, textAlign: align }} title={s.value ?? ''}>
        {roDisplay(code, s)}</span>;
    }
    return <BlankInput value={s.value} auto={s.auto} isManual={s.is_manual}
      kind={s.kind} align={align} error={isErr?.(code)}
      onSave={(v) => save(code, v)} onReset={() => reset(code)} />;
  };
  const has = (c: string) => byCode.has(c);
  const lbl = (c: string) => byCode.get(c)?.label ?? '';
  const sub: CSSProperties = { fontSize: '0.62rem', color: '#555', textAlign: 'center' };
  const row = (code: string, align: 'left' | 'right' | 'center' = 'left') => {
    const s = byCode.get(code);
    if (!s) return null;
    return <tr><td style={numCell} title={code}>{s.num ?? ''}</td>
      <td style={nameCell}>{s.label}</td><td style={valCell}>{inp(code, align)}</td></tr>;
  };
  // назва додатка з бланка (перший рядок блока 01)
  const APP01: Record<string, string> = {
    D1: 'Відомості про нарахування заробітної плати (доходу, грошового забезпечення) застрахованим особам',
    '4DF': 'Відомості про суми нарахованого доходу, утриманого та сплаченого податку на доходи фізичних осіб та військового збору',
    D5: 'Відомості про трудові відносини осіб та період проходження військової служби',
    D2: 'Відомості про осіб, які доглядають за дитиною до досягнення нею '
      + 'трирічного віку та відповідно до закону отримують допомогу по догляду '
      + 'за дитиною до досягнення нею трирічного віку та/або при народженні '
      + 'дитини, усиновленні дитини, та осіб із числа непрацюючих працездатних '
      + 'батьків, усиновителів, опікунів, піклувальників, прийомних батьків, '
      + 'батьків-вихователів, які фактично здійснюють догляд за дитиною з '
      + 'інвалідністю, дитиною, хворою на тяжке перинатальне ураження нервової '
      + 'системи, тяжку вроджену ваду розвитку, рідкісне орфанне захворювання, '
      + 'онкологічне, онкогематологічне захворювання, дитячий церебральний '
      + 'параліч, тяжкий психічний розлад, цукровий діабет I типу '
      + '(інсулінозалежний), гостре або хронічне захворювання нирок IV ступеня, '
      + 'за дитиною, яка отримала тяжку травму, потребує трансплантації органа, '
      + 'потребує паліативної допомоги, якій не встановлено інвалідність, а '
      + 'також непрацюючих працездатних осіб, які здійснюють догляд за особою з '
      + 'інвалідністю I групи або за особою похилого віку, яка за висновком '
      + 'медичного закладу потребує постійного стороннього догляду або досягла '
      + '80-річного віку, якщо такі непрацюючі працездатні особи отримують '
      + 'допомогу, надбавку або компенсацію відповідно до законодавства, та '
      + 'нарахування сум єдиного внеску за патронатних вихователів, '
      + 'батьків-вихователів дитячих будинків сімейного типу, прийомних '
      + 'батьків, якщо вони отримують грошове забезпечення відповідно до '
      + 'законодавства',
    D3: 'Відомості про осіб, які проходять строкову військову службу',
    D6: 'Відомості про наявність підстав для обліку стажу окремим категоріям '
      + 'осіб відповідно до законодавства',
  };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, border: B }}>
      <colgroup><col style={{ width: 46 }} /><col /><col style={{ width: 260 }} /></colgroup>
      <tbody>
        <tr>
          <td style={numCell}>01</td>
          <td style={nameCell}>{APP01[form.code] ?? form.title}</td>
          <td style={{ ...valCell, padding: 0 }}>
            {(['HZ', 'HZN', 'HZU'] as const).map((c) => (
              <div key={c} style={{ display: 'flex', borderBottom: c !== 'HZU' ? B : 'none' }}>
                <div style={{ ...numCell, width: 34, border: 'none', borderRight: B }}>{byCode.get(c)?.num}</div>
                <div style={{ flex: 1, padding: '2px 4px', fontSize: '0.74rem' }}>{lbl(c).replace(/^0\d\d\s*/, '')}</div>
                <div style={{ width: 60, borderLeft: B }}>{inp(c, 'center')}</div>
              </div>
            ))}
          </td>
        </tr>
        <tr>
          <td style={numCell}>02</td>
          <td style={nameCell}>Звітний (податковий) період</td>
          <td style={{ ...valCell, padding: 0 }}>
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1, borderRight: B }}>{inp('HZY', 'center')}<div style={sub}>(рік)</div></div>
              <div style={{ flex: 1 }}>{inp('HZM', 'center')}<div style={sub}>(місяць)</div></div>
            </div>
          </td>
        </tr>
        <tr>
          <td style={numCell}>03</td>
          <td style={nameCell} colSpan={2}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ whiteSpace: 'nowrap' }}>Платник єдиного внеску</span>
              <span style={{ flex: 1 }}>{inp('HNAME')}</span>
            </div>
            {(has('HDDGV') || has('HNDGV')) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                <span style={{ fontSize: '0.72rem' }}>від</span>
                <span style={{ width: 110 }}>{inp('HDDGV', 'center')}</span>
                <span style={{ fontSize: '0.72rem' }}>№</span>
                <span style={{ width: 130 }}>{inp('HNDGV')}</span>
              </div>
            )}
          </td>
        </tr>
        {has('HNUM') && row('HNUM', 'center')}
        {row('HTIN')}
        {has('HKATOTTG') && row('HKATOTTG')}
        {has('HTIN1') && row('HTIN1')}
        {has('HFIL') && row('HFIL')}
        {has('R00G01I') && row('R00G01I', 'right')}
        {has('R00G02I') && row('R00G02I', 'right')}
      </tbody>
    </table>
  );
}

/** Підвал бланка (спільний для всіх форм): дата подання + підписанти. */
function BlankFooter({
  byCode, save, reset,
}: {
  byCode: Map<string, TaxFormScalar>;
  save: (code: string, v: string) => Promise<void>;
  reset: (code: string) => Promise<void>;
}) {
  const inp = (code: string) => {
    const s = byCode.get(code);
    if (!s) return null;
    if (!s.editable) return <span style={roStyle}>
      {s.kind === 'date' ? (dateToHuman(s.value) || '') : (s.value ?? '')}</span>;
    return <BlankInput value={s.value} auto={s.auto} isManual={s.is_manual}
      kind={s.kind} align={s.kind === 'date' ? 'center' : 'left'}
      onSave={(v) => save(code, v)} onReset={() => reset(code)} />;
  };
  const box: CSSProperties = { border: B, minWidth: 150, padding: '1px 3px' };
  const cap: CSSProperties = { fontSize: '0.6rem', color: '#666', textAlign: 'center' };
  const signer = (name: string, kCode: string, nCode: string) => (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginTop: 8,
      flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 260px', fontSize: '0.74rem' }}>{name}</div>
      <div>
        <div style={box}>{inp(kCode)}</div>
        <div style={cap}>(РНОКПП або серія та № паспорта)</div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ borderBottom: B, width: 90, height: 16 }} />
        <div style={cap}>(підпис)</div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={box}>{inp(nCode)}</div>
        <div style={cap}>(власне ім'я та прізвище)</div>
      </div>
    </div>
  );
  return (
    <div style={{ marginTop: '0.6rem', fontFamily: SERIF }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: '0.74rem' }}>Дата подання</span>
        <span style={{ ...box, width: 120 }}>{inp('HFILL')}</span>
        <span style={{ fontSize: '0.72rem', color: '#333', marginLeft: 'auto' }}>
          Наведена інформація є повною і достовірною.
        </span>
      </div>
      {signer('Керівник (уповноважена особа) / фізична особа (законний представник)', 'HKBOS', 'HBOS')}
      <div style={{ textAlign: 'center', fontSize: '0.66rem', color: '#555',
        margin: '4px 0' }}>М. П. (за наявності)</div>
      {signer('Головний бухгалтер (особа, відповідальна за ведення бухгалтерського обліку)', 'HKBUH', 'HBUH')}
    </div>
  );
}

/** Ширина графи таблиці додатка за типом даних. */
const WIDE_TEXT = new Set([
  'T1RXXXXG111S', 'T1RXXXXG112S', 'T1RXXXXG113S',
  'T1RXXXXG91S', 'T1RXXXXG92S', 'T1RXXXXG93S',
  'T1RXXXXG61S', 'T1RXXXXG62S', 'T1RXXXXG63S',
  'T1RXXXXG71S', 'T1RXXXXG72S', 'T1RXXXXG73S',
  'T1RXXXXG81S', 'T1RXXXXG82S', 'T1RXXXXG83S',
  'T1RXXXXG13S', 'T1RXXXXG15S', 'T1RXXXXG16S',
  'T1RXXXXG17S', 'T1RXXXXG19S', 'T1RXXXXG14S',
]);
const RNOKPP = new Set([
  'T1RXXXXG7S', 'T1RXXXXG8S', 'T1RXXXXG6S', 'T1RXXXXG5S', 'T1RXXXXG02',
]);
function colWidth(c: TaxFormColumn): number {
  if (WIDE_TEXT.has(c.tag)) return 150;
  if (c.kind === 'date') return 84;
  if (RNOKPP.has(c.tag)) return 100;
  const g = c.tag.replace(/^T\d?RXXXX/, '');
  if (/^G(1[4-9]|20|03A?|03|04A?|04|5A?|5|12|13)$/.test(g)) return 92;
  return 52;
}

/** Таблиця додатка у стилі бланка (обрамлена, шапка з номером графи).
 * totals: мапа «тег колонки → код підсумку ('X' = не підсумовується)» —
 * рядок «Усього» рендериться ВСЕРЕДИНІ таблиці у тих самих колонках. */
function BlankAppendixTable({
  repId, section, table, onChanged, totals, byCode, saveScalar, resetScalar,
  isErr,
}: {
  repId: string;
  section: string;
  table: TaxFormTable;
  onChanged: () => void;
  totals?: Record<string, string>;
  byCode?: Map<string, TaxFormScalar>;
  saveScalar?: (code: string, v: string) => Promise<void>;
  resetScalar?: (code: string) => Promise<void>;
  isErr?: (field: string, row?: number) => boolean;
}) {
  async function saveCell(tag: string, rowNum: number, v: string) {
    await taxReportJ05.setLine(repId, { section, row_code: tag, row_num: rowNum, value_text: v });
    onChanged();
  }
  async function resetCell(tag: string, rowNum: number) {
    await taxReportJ05.setLine(repId, { section, row_code: tag, row_num: rowNum, value_text: null });
    onChanged();
  }
  const nextRow = table.rows.length + 1;

  const totalCell = (tag: string) => {
    const tcode = totals?.[tag];
    if (!tcode) return <td key={tag} style={{ border: B }} />;
    if (tcode === 'X') {
      return <td key={tag} style={{ border: B, textAlign: 'center',
        fontWeight: 600 }}>X</td>;
    }
    const s = byCode?.get(tcode);
    if (!s) return <td key={tag} style={{ border: B }} />;
    return (
      <td key={tag} style={{ border: B, padding: '0 1px' }}
        title={s.label}>
        {s.editable && saveScalar && resetScalar ? (
          <BlankInput value={s.value} auto={s.auto} isManual={s.is_manual}
            kind={s.kind} align="right"
            onSave={(v) => saveScalar(tcode, v)}
            onReset={() => resetScalar(tcode)} />
        ) : (
          <span style={{ ...roStyle, textAlign: 'right' }}>{s.value ?? '-'}</span>
        )}
      </td>
    );
  };
  return (
    <div style={{ overflowX: 'auto', marginTop: '0.6rem' }}>
      <table style={{ ...blankBox, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f3f4f6' }}>
            <th style={{ ...numCell, ...thBlank, verticalAlign: 'top' }}>№</th>
            {table.columns.map((c) => {
              const w = colWidth(c);
              return (
                <th key={c.tag} style={{ ...thBlank, border: B, padding: '2px 4px',
                  width: w, minWidth: w, maxWidth: w,
                  lineHeight: 1.15, verticalAlign: 'top',
                  textAlign: 'center', whiteSpace: 'normal' }} title={c.tag}>
                  {c.num && <div style={{ fontWeight: 700 }}>{c.num}</div>}
                  {c.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r) => (
            <tr key={r.row_num}>
              <td style={numCell}>{r.row_num}</td>
              {table.columns.map((c) => {
                const cell = r.cells[c.tag];
                return (
                  <td key={c.tag} style={{ border: B, padding: '0 1px' }}>
                    <BlankInput
                      value={cell?.value ?? null}
                      auto={cell?.auto ?? null}
                      isManual={cell?.is_manual ?? false}
                      kind={c.kind}
                      align={c.kind === 'date' ? 'center'
                        : (isNum(cell?.value ?? null) ? 'right' : 'left')}
                      error={isErr?.(c.tag, r.row_num)}
                      onSave={(v) => saveCell(c.tag, r.row_num, v)}
                      onReset={() => resetCell(c.tag, r.row_num)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
          {/* Порожня таблиця (ручний/порожній розрахунок) — показуємо один
              порожній рядок: заповнення клітинки створює рядок №1. */}
          {table.rows.length === 0 && (
            <tr>
              <td style={numCell}>1</td>
              {table.columns.map((c) => (
                <td key={c.tag} style={{ border: B, padding: '0 1px' }}>
                  <BlankInput
                    value={null} auto={null} isManual={false} kind={c.kind}
                    align={c.kind === 'date' ? 'center' : 'left'}
                    error={isErr?.(c.tag, 1)}
                    onSave={(v) => saveCell(c.tag, 1, v)}
                    onReset={() => resetCell(c.tag, 1)}
                  />
                </td>
              ))}
            </tr>
          )}
          {/* «Усього» — у тих самих колонках, що й розрахунок (як у бланку) */}
          {totals && (
            <tr style={{ background: '#f3f4f6' }}>
              <td style={{ ...numCell, fontWeight: 700, whiteSpace: 'nowrap' }}>
                Усього
              </td>
              {table.columns.map((c) => totalCell(c.tag))}
            </tr>
          )}
        </tbody>
      </table>
      <button
        style={{ marginTop: '0.4rem' }}
        onClick={() => { const c = table.columns[0]; if (c) saveCell(c.tag, nextRow, ''); }}
        title="Додати ручний рядок у кінець таблиці"
      >+ рядок</button>
    </div>
  );
}

// Рядок «Усього» ВСЕРЕДИНІ таблиці розділу I: підсумок у ТІЙ САМІЙ колонці,
// де йде розрахунок; 'X' — колонки, що у бланку не підсумовуються.
const TOTALS_BY_COL: Record<string, Record<string, string>> = {
  D1: {
    T1RXXXXG16: 'R01G16', T1RXXXXG17: 'R01G17', T1RXXXXG18: 'R01G18',
    T1RXXXXG19: 'R01G19', T1RXXXXG20: 'R01G20',
    T1RXXXXG21: 'X', T1RXXXXG22: 'X', T1RXXXXG23: 'X',
    T1RXXXXG24: 'X', T1RXXXXG25: 'X', T1RXXXXG26: 'X',
  },
  '4DF': {
    T1RXXXXG03A: 'R01G03A', T1RXXXXG03: 'R01G03',
    T1RXXXXG04A: 'R01G04A', T1RXXXXG04: 'R01G04',
    T1RXXXXG5A: 'R01G5A', T1RXXXXG5: 'R01G5',
    T1RXXXXG02: 'X', T1RXXXXG05: 'X', T1RXXXXG06D: 'X',
    T1RXXXXG07D: 'X', T1RXXXXG08: 'X', T1RXXXXG09: 'X',
  },
  D2: {
    T1RXXXXG14: 'R01G14', T1RXXXXG15: 'R01G15', T1RXXXXG16: 'R01G16',
    T1RXXXXG17: 'X',
  },
  D3: {
    T1RXXXXG12: 'R01G12', T1RXXXXG13: 'R01G13', T1RXXXXG14: 'X',
  },
};
const DF_COUNTERS = ['R02G01I', 'R02G02I', 'R02G03I'];

// --- Матриці показників (розкладка точно за бланком) ---
// Клітинка: код тега, 'X' (не заповнюється) або null (порожня).
type MatrixRow = { num?: string; name: string; cells: (string | null)[] };
type MatrixSpec = { title?: string; colHeads: string[][]; rows: MatrixRow[];
  nameWidth?: number };

/** 4ДФ розділ II: 6 рядків × 6 колонок (нарах./виплач. × дохід/ПДФО/ВЗ). */
const DF_S2: MatrixSpec = {
  title: 'Розділ ІІ. Зведені дані про оподаткування процентів, виграшів (призів) у лотерею',
  colHeads: [
    ['Загальна сума доходу', 'нарахованого', 'виплаченого'],
    ['Загальна сума податку на доходи фізичних осіб', 'нарахованого', 'перерахованого'],
    ['Загальна сума військового збору', 'нарахованого', 'перерахованого'],
  ],
  rows: [
    { name: 'Оподаткування процентів',
      cells: ['R0201G03A', 'X', 'R0201G04A', 'R0201G04', 'R0201G5A', 'R0201G5'] },
    { name: 'Оподаткування процентів - виключення',
      cells: ['R0202G03A', 'X', 'R0202G04A', 'R0202G04', 'R0202G5A', 'R0202G5'] },
    { name: 'Оподаткування виграшів (призів) у лотерею',
      cells: ['R0203G03A', 'R0203G03', 'R0203G04A', 'R0203G04', 'R0203G5A', 'R0203G5'] },
    { name: 'Оподаткування виграшів (призів) у лотерею - виключення',
      cells: ['R0204G03A', 'R0204G03', 'R0204G04A', 'R0204G04', 'R0204G5A', 'R0204G5'] },
    { name: 'Військовий збір',
      cells: ['R0205G03A', 'R0205G03', 'R0205G04A', 'R0205G04', 'R0205G5A', 'R0205G5'] },
    { name: 'Військовий збір-виключення',
      cells: ['R0206G03A', 'R0206G03', 'R0206G04A', 'R0206G04', 'R0206G5A', 'R0206G5'] },
  ],
};

/** Д2: верхня таблиця показників 1–6.1 (чисельність + ЄСВ по періодах). */
const D2_MATRIX: MatrixSpec = {
  colHeads: [
    ['Чисельність осіб, яким нарахована виплата у звітному місяці'],
    ['Нараховано єдиного внеску (грн)', 'у звітному місяці',
      'у тому числі за попередні періоди', 'з початку року'],
  ],
  nameWidth: 420,
  rows: [
    { num: '1', name: 'Особи, які доглядають за дитиною до досягнення нею '
      + 'трирічного віку та відповідно до закону отримують допомогу по догляду '
      + 'за дитиною до досягнення нею трирічного віку та/або допомогу при '
      + 'народженні дитини, при усиновленні дитини',
      cells: ['R01G3', 'R01G4', 'R01G5', 'R01G6'] },
    { num: '2', name: 'Непрацюючі працездатні батьки, усиновителі опікунів, '
      + 'піклувальників, прийомних батьків, батьків-вихователів, які фактично '
      + 'здійснюють догляд за дитиною з інвалідністю, дитиною, хворою на тяжке '
      + 'перинатальне ураження нервової системи, тяжку вроджену ваду розвитку, '
      + 'рідкісне орфанне захворювання, онкологічне, онкогематологічне '
      + 'захворювання, дитячий церебральний параліч, тяжкий психічний розлад, '
      + 'цукровий діабет I типу (інсулінозалежний), гостре або хронічне '
      + 'захворювання нирок IV ступеня, за дитиною, яка отримала тяжку травму, '
      + 'потребує трансплантації органа, потребує паліативної допомоги, якій не '
      + 'встановлено інвалідності, якщо такі непрацюючі працездатні особи '
      + 'отримують допомогу, надбавку або компенсацію відповідно до законодавства',
      cells: ['R02G3', 'R02G4', 'R02G5', 'R02G6'] },
    { num: '3', name: 'Непрацюючі працездатні особи, що здійснюють догляд за '
      + 'особою з інвалідністю I групи та отримують допомогу, надбавку або '
      + 'компенсацію відповідно до законодавства',
      cells: ['R03G3', 'R03G4', 'R03G5', 'R03G6'] },
    { num: '4', name: 'Непрацюючі працездатні особи, які здійснюють догляд '
      + 'особою похилого віку, яка згідно з висновком медичного закладу потребує '
      + 'постійного стороннього догляду або досягла 80-річного віку, та '
      + 'отримують допомогу, надбавку або компенсацію відповідно до законодавства',
      cells: ['R04G3', 'R04G4', 'R04G5', 'R04G6'] },
    { num: '5', name: 'Разом (рядки 1 + 2 + 3 + 4)',
      cells: ['X', 'R05G4', 'R05G5', 'R05G6'] },
    { num: '6', name: 'Патронатні вихователі, батьки-вихователі дитячих будинків '
      + 'сімейного типу та прийомні батьки, що отримують грошове забезпечення '
      + 'відповідно до законодавства, у тому числі',
      cells: ['R06G3', 'X', 'X', 'X'] },
    { num: '6.1', name: 'Патронатні вихователі, батьки-вихователі дитячих '
      + 'будинків сімейного типу та прийомні батьки, що отримують грошове '
      + 'забезпечення відповідно до законодавства, які не працюють',
      cells: ['R061G3', 'X', 'X', 'X'] },
  ],
};

/** Д3: верхня таблиця (чисельність + ЄСВ по періодах, один рядок). */
const D3_MATRIX: MatrixSpec = {
  colHeads: [
    ['Чисельність осіб, яким нараховане грошове забезпечення у звітному місяці'],
    ['Нараховано єдиного внеску (грн)', 'у звітному місяці',
      'у тому числі за попередні звітні періоди', 'з початку року'],
  ],
  nameWidth: 0,
  rows: [
    { name: '', cells: ['R01G1', 'R01G2', 'R01G3', 'R01G4'] },
  ],
};

/** Таблиця-матриця показників за бланком (Д2/Д3 шапка, 4ДФ розділ II). */
function MatrixBlock({
  spec, byCode, save, reset,
}: {
  spec: MatrixSpec;
  byCode: Map<string, TaxFormScalar>;
  save: (code: string, v: string) => Promise<void>;
  reset: (code: string) => Promise<void>;
}) {
  const cell = (code: string | null) => {
    if (code === 'X') {
      return <span style={{ display: 'block', textAlign: 'center',
        fontWeight: 600 }}>X</span>;
    }
    if (!code) return null;
    const s = byCode.get(code);
    if (!s) return null;
    if (!s.editable) return <span style={{ ...roStyle, textAlign: 'right' }}>{s.value ?? '-'}</span>;
    return <BlankInput value={s.value} auto={s.auto} isManual={s.is_manual}
      kind={s.kind} align="right" onSave={(v) => save(code, v)} onReset={() => reset(code)} />;
  };
  // Заголовки: групи (перший елемент) + підколонки (решта).
  const groups = spec.colHeads;
  const hasName = spec.nameWidth !== 0;
  return (
    <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, border: B }}>
        <tbody>
          {spec.title && (
            <tr style={{ background: '#eef2f7' }}>
              <td colSpan={99} style={{ ...nameCell, fontWeight: 700 }}>{spec.title}</td>
            </tr>
          )}
          {/* Рядок груп колонок */}
          <tr style={{ background: '#f3f4f6' }}>
            {hasName && (
              <td rowSpan={2} style={{ ...nameCell, fontWeight: 600,
                width: spec.nameWidth, textAlign: 'center', verticalAlign: 'middle' }}>
                {spec.rows.some((r) => r.num) ? 'Назва показника' : 'Показник'}
              </td>
            )}
            {groups.map((g, i) => (
              <td key={i} colSpan={Math.max(1, g.length - 1)}
                style={{ ...nameCell, fontSize: '0.72rem', fontWeight: 600,
                  textAlign: 'center' }}>
                {g[0]}
              </td>
            ))}
          </tr>
          {/* Рядок підколонок */}
          <tr style={{ background: '#f3f4f6' }}>
            {groups.flatMap((g, i) => (g.length > 1 ? g.slice(1) : [''])
              .map((h, j) => (
                <td key={`${i}-${j}`} style={{ ...nameCell, fontSize: '0.68rem',
                  textAlign: 'center' }}>{h}</td>
              )))}
          </tr>
          {spec.rows.map((r, ri) => (
            <tr key={ri}>
              {hasName && (
                <td style={{ ...nameCell, fontSize: '0.72rem' }}>
                  {r.num && <b style={{ marginRight: 4 }}>{r.num}</b>}{r.name}
                </td>
              )}
              {r.cells.map((c, ci) => (
                <td key={ci} style={{ ...valCell, width: 100 }}
                  title={c && c !== 'X' ? (byCode.get(c)?.label ?? c) : undefined}>
                  {cell(c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Блок «Усього» + додаткові розділи під таблицею додатка. */
function AppendixTotals({
  form, byCode, save, reset,
}: {
  form: TaxFormView;
  byCode: Map<string, TaxFormScalar>;
  save: (code: string, v: string) => Promise<void>;
  reset: (code: string) => Promise<void>;
}) {
  const cell = (code: string) => {
    const s = byCode.get(code);
    if (!s) return null;
    if (!s.editable) return <span style={{ ...roStyle, textAlign: 'right' }}>{s.value ?? '—'}</span>;
    return <BlankInput value={s.value} auto={s.auto} isManual={s.is_manual}
      kind={s.kind} align="right" onSave={(v) => save(code, v)} onReset={() => reset(code)} />;
  };
  const isDf = form.code === '4DF';
  const counters = isDf ? DF_COUNTERS.filter((c) => byCode.has(c)) : [];
  if (!isDf && !counters.length) return null;

  return (
    <>
      {/* Розділ II 4ДФ — матриця 6×6 (проценти/виграші/ВЗ) за бланком */}
      {isDf && (
        <MatrixBlock spec={DF_S2} byCode={byCode} save={save} reset={reset} />
      )}
      {/* Лічильники 4ДФ */}
      {counters.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox,
          border: B, marginTop: '0.5rem' }}>
          <tbody>
            <tr>
              {counters.map((c) => (
                <td key={c} style={{ ...nameCell }}>
                  {byCode.get(c)?.label}: {cell(c)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      )}
    </>
  );
}

export default function TaxReportJ05FormView({ repId, errors }: {
  repId: string;
  errors?: { form: string; field?: string | null; row?: number | null }[];
}) {
  const [forms, setForms] = useState<TaxFormView[] | null>(null);
  const [active, setActive] = useState<string>('MAIN');
  const [error, setError] = useState<string | null>(null);

  function reload() {
    taxReportJ05.form(repId)
      .then((r) => setForms(r.forms))
      .catch((e) => setError(e?.response?.data?.detail ?? 'Помилка завантаження форми'));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [repId]);

  if (error) {
    return <div style={{ background: '#fee', color: '#b00',
      padding: '0.5rem 0.75rem', borderRadius: 6 }}>{error}</div>;
  }
  if (!forms || forms.length === 0) {
    return <div style={{ color: 'var(--muted)' }}>Завантаження форми…</div>;
  }

  const form = forms.find((f) => f.code === active) ?? forms[0]!;

  async function saveScalar(code: string, v: string) {
    await taxReportJ05.setLine(repId, { section: form.code, row_code: code, row_num: null, value_text: v });
    reload();
  }
  async function resetScalar(code: string) {
    await taxReportJ05.setLine(repId, { section: form.code, row_code: code, row_num: null, value_text: null });
    reload();
  }

  const byCode = new Map(form.scalars.map((s) => [s.code, s]));

  // Ключі полів з помилками перевірки: "FORM:FIELD" або "FORM:FIELD:ROW".
  const errKeys = new Set(
    (errors ?? []).filter((e) => e.field).map((e) =>
      e.row != null ? `${e.form}:${e.field}:${e.row}` : `${e.form}:${e.field}`),
  );
  const isErr = (field: string, row?: number) =>
    errKeys.has(row != null ? `${form.code}:${field}:${row}` : `${form.code}:${field}`);

  // Рядки розділів I/II/III основної форми (суми) — окремою таблицею.
  const isSectionRow = (s: TaxFormScalar) => /^R0[123]\d/.test(s.code);
  const rowScalars = form.scalars.filter((s) => isSectionRow(s));

  // Розділ рядка — за кодом (R01xx → І, R02xx → ІІ, R03xx → ІІІ);
  // заголовки — дослівно за бланком.
  const sectionTitle = (s: TaxFormScalar): string | null => {
    const p = s.code.slice(1, 3);
    if (p === '01') return 'І. Нарахування доходу та єдиного внеску за найманих працівників (грн, коп.)';
    if (p === '02') return 'ІІ. Нарахування грошового забезпечення та єдиного внеску за військовослужбовців, '
      + 'поліцейських, осіб рядового і начальницького складу та на суми допомоги військовослужбовцям-жінкам '
      + 'у зв\'язку з вагітністю та пологами (крім військовослужбовців строкової військової служби) (грн, коп.)';
    if (p === '03') return 'ІІІ. Нарахування грошового забезпечення та єдиного внеску за патронатних '
      + 'вихователів, батьків-вихователів та прийомних батьків (грн, коп.)';
    return null;
  };

  return (
    <div style={{ marginTop: '0.5rem' }}>
      {/* Вкладки форм пакета — стиль сторінки організації */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div className="seg-tabs">
          {forms.map((f) => (
            <button key={f.code} type="button"
              className={f.code === form.code ? 'active' : ''}
              onClick={() => setActive(f.code)} title={f.title}>
              {TAB_NAME[f.code] ?? f.code}
            </button>
          ))}
        </div>
      </div>

      {/* Бланк форми */}
      <div style={{ ...blankBox, maxWidth: 900, margin: '0.7rem 0',
        padding: '0.9rem 1rem' }}>
        {/* Службова шапка бланка */}
        <div style={{ display: 'flex', justifyContent: 'space-between',
          fontSize: '0.72rem', color: '#333', marginBottom: '0.6rem' }}>
          <div style={{ textAlign: 'center', width: 220 }}>
            Відмітка про отримання<br />
            <span style={{ fontSize: '0.66rem' }}>(штамп контролюючого органу)</span>
          </div>
          {form.code === 'MAIN' ? (
            <div style={{ textAlign: 'right', fontSize: '0.68rem', lineHeight: 1.35 }}>
              ЗАТВЕРДЖЕНО<br />
              Наказ Міністерства фінансів України<br />
              13 січня 2015 року № 4<br />
              (у редакції наказу від 24 січня 2025 року № 39)
            </div>
          ) : (
            <div style={{ textAlign: 'right', fontSize: '0.68rem', lineHeight: 1.35, maxWidth: 280 }}>
              <b>Додаток {TAB_NAME[form.code] ?? form.code}</b><br />
              до Податкового розрахунку сум доходу, нарахованого (сплаченого)
              на користь платників податків — фізичних осіб, і сум утриманого
              з них податку, а також сум нарахованого єдиного внеску
            </div>
          )}
        </div>
        {form.code === 'MAIN' && (
          <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.95rem',
            marginBottom: '0.7rem' }}>
            {form.title}
          </div>
        )}

        {/* Шапка реквізитів: основна форма — повна секційна розкладка бланка;
            додатки — коротка шапка (тип/період/платник/номер). */}
        {form.code === 'MAIN' ? (
          <MainHeader byCode={byCode} save={saveScalar} reset={resetScalar}
            isErr={isErr} />
        ) : (
          <AppendixHeader form={form} byCode={byCode} save={saveScalar}
            reset={resetScalar} isErr={isErr} />
        )}

        {/* Основна форма: розділи I/II/III — таблиця показників */}
        {form.code === 'MAIN' && rowScalars.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox,
            border: B, marginTop: '0.6rem' }}>
            <colgroup>
              <col style={{ width: 46 }} />
              <col />
              <col style={{ width: 200 }} />
            </colgroup>
            <tbody>
              {rowScalars.map((s, i) => {
                const title = sectionTitle(s);
                const prevTitle = i > 0 ? sectionTitle(rowScalars[i - 1]!) : null;
                const rows = [];
                if (title && title !== prevTitle) {
                  rows.push(
                    <tr key={`h-${title}`} style={{ background: '#eef2f7' }}>
                      <td colSpan={3} style={{ ...nameCell, fontWeight: 700,
                        textAlign: 'center' }}>{title}</td>
                    </tr>,
                  );
                }
                rows.push(
                  <BlankRow key={s.code} s={s} error={isErr(s.code)}
                    onSave={saveScalar} onReset={resetScalar} />,
                );
                return rows;
              })}
            </tbody>
          </table>
        )}

        {/* Доповнення до Розрахунку (п.46.4 ст.46 ПКУ) — лише основна форма */}
        {form.code === 'MAIN' && (
          <div style={{ marginTop: '0.6rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6,
              fontFamily: SERIF, fontSize: '0.78rem' }}>
              <span>
                Доповнення до цього Розрахунку (заповнюється і подається
                відповідно до пункту 46.4 статті 46 глави 2 розділу ІІ
                Податкового кодексу України) на
              </span>
              <span style={{ width: 60, border: B }}>
                {(() => {
                  const s = byCode.get('HJAR');
                  if (!s) return null;
                  return <BlankInput value={s.value} auto={s.auto}
                    isManual={s.is_manual} align="center"
                    onSave={(v) => saveScalar('HJAR', v)}
                    onReset={() => resetScalar('HJAR')} />;
                })()}
              </span>
              <span>арк.</span>
            </div>
            {form.table && (
              <BlankAppendixTable repId={repId} section={form.code}
                table={form.table} onChanged={reload} isErr={isErr} />
            )}
          </div>
        )}

        {/* Додаток: таблиця розділу I (по особах/подіях) + «Усього» + розділи */}
        {form.code !== 'MAIN' && (() => {
          const tbls = form.tables ?? (form.table ? [{ key: 'main', ...form.table }] : []);
          const mainTbl = tbls.find((t) => t.key === 'main') ?? tbls[0];
          const grantTbl = tbls.find((t) => t.key === 'grants');
          return (
            <>
              {/* Д2/Д3: верхня таблиця показників (чисельність + ЄСВ) за бланком */}
              {form.code === 'D2' && (
                <MatrixBlock spec={D2_MATRIX} byCode={byCode}
                  save={saveScalar} reset={resetScalar} />
              )}
              {form.code === 'D3' && (
                <MatrixBlock spec={D3_MATRIX} byCode={byCode}
                  save={saveScalar} reset={resetScalar} />
              )}
              {mainTbl && (
                <BlankAppendixTable repId={repId} section={form.code}
                  table={mainTbl} onChanged={reload}
                  totals={TOTALS_BY_COL[form.code]} byCode={byCode}
                  saveScalar={saveScalar} resetScalar={resetScalar}
                  isErr={isErr} />
              )}
              <AppendixTotals form={form} byCode={byCode}
                save={saveScalar} reset={resetScalar} />
              {grantTbl && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ ...blankBox, borderBottom: 'none', padding: '3px 6px',
                    fontWeight: 700, fontSize: '0.78rem', background: '#eef2f7' }}>
                    Розділ III. Розгорнута інформація про бюджетні гранти
                  </div>
                  <BlankAppendixTable repId={repId} section={form.code}
                    table={grantTbl} onChanged={reload} />
                </div>
              )}
            </>
          );
        })()}

        {/* Підвал бланка: дата подання + підписанти */}
        <BlankFooter byCode={byCode} save={saveScalar} reset={resetScalar} />
      </div>

      <p style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
        Значення автозаповнені з нарахувань ЗП і кадрів. Жовте поле —
        виправлено вручну (перекриває авто у XML), ↺ — повернути авто.
      </p>
    </div>
  );
}
